/**
 * Top-level Kanban board.
 *
 * Five columns (BACKLOG, TODAY, DOING, WAITING, DONE) drawn from
 * the active tasks query. dnd-kit DndContext spans the whole board
 * so cards can drag between columns. The move endpoint receives the
 * new status; the optimistic update flips the cache immediately and
 * the success invalidation refetches the canonical order.
 *
 * Card clicks open the slide-over via the shared `useSlideOver`
 * store. The "+ ADD TASK" affordance per column opens the quick-
 * capture pre-filled with that column's status.
 *
 * Reference: spec section 15.4, Claude Design Files README 5.2.
 */

import { useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  KeyboardSensor,
  type DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { Task, TaskStatus } from '@workspace/shared';

import { useTasks, useMoveTask } from '@/lib/api/tasks';
import { useSettings } from '@/lib/api/settings';
import { useSlideOver } from '@/lib/slide-over';
import { todayIso } from '@/lib/utils';
import { TaskColumn } from './TaskColumn';
import { QuickCapture } from './QuickCapture';

/**
 * Section 4.7 default WIP limit. Used as the fallback while the
 * settings query is loading so the board does not flicker between an
 * arbitrary number and the real value on first render.
 */
const FALLBACK_WIP_LIMIT_DOING = 3;

export function TaskBoard() {
  // UAT-45 daily refresh: the Board passes today's local ISO date so
  // the list response also carries DONE rows completed since midnight.
  // Tomorrow's render passes a new value and yesterday's completions
  // drop out of the response naturally; the rows themselves stay in the
  // DB and remain reachable from the Today and Table views.
  const { data, isLoading, isError } = useTasks({ includeCompletedSince: todayIso() });
  const { data: settings } = useSettings();
  const moveTask = useMoveTask();
  const openSlideOver = useSlideOver((s) => s.open);
  const [addingFor, setAddingFor] = useState<TaskStatus | null>(null);

  // Doing-column WIP limit is settings-driven (UAT 2.3-2.5). While the
  // settings query is in flight we render the canonical default so the
  // column header does not show a transient "3" then flip to the user's
  // configured value.
  const wipLimitDoing = settings?.['tasks.wipLimitDoing'] ?? FALLBACK_WIP_LIMIT_DOING;
  const COLUMNS: { status: TaskStatus; label: string; wipLimit: number | null }[] = [
    { status: 'BACKLOG', label: 'BACKLOG', wipLimit: null },
    { status: 'TODAY', label: 'TODAY', wipLimit: null },
    { status: 'DOING', label: 'DOING', wipLimit: wipLimitDoing },
    { status: 'WAITING', label: 'WAITING', wipLimit: null },
    { status: 'DONE', label: 'DONE', wipLimit: null },
  ];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (isLoading) {
    return <p className="mono-label text-mx-grey-mid">LOADING…</p>;
  }
  if (isError) {
    return <p className="text-mx-body-sm text-mx-neg">Could not load tasks.</p>;
  }
  const tasks = data?.tasks ?? [];

  // Bucket tasks by column. The within-column order honours the
  // server's response (the route applies the section-5.2 composite
  // sort plus manual orderIndex precedence).
  const byColumn = new Map<TaskStatus, Task[]>();
  for (const col of COLUMNS) byColumn.set(col.status, []);
  for (const task of tasks) {
    byColumn.get(task.status)?.push(task);
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;
    const draggedId = String(active.id);
    const dragged = tasks.find((t) => t.id === draggedId);
    if (!dragged) return;

    // The droppable target is either a column (drop-zone) or another
    // card (sortable). Resolve to a status string in both cases.
    const overData = over.data?.current as { status?: TaskStatus } | undefined;
    const targetStatus = overData?.status ?? dragged.status;
    if (targetStatus === dragged.status && active.id === over.id) return;

    moveTask.mutate({ id: draggedId, status: targetStatus });
  }

  return (
    <div className="flex h-[calc(100vh-12rem)] flex-col gap-mx-4">
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="flex flex-1 overflow-x-auto border-t border-mx-grey-800">
          {COLUMNS.map((col) => (
            <TaskColumn
              key={col.status}
              status={col.status}
              label={col.label}
              tasks={byColumn.get(col.status) ?? []}
              wipLimit={col.wipLimit}
              autoPulledTaskId={null}
              onCardClick={(id) => openSlideOver({ kind: 'task', taskId: id })}
              onAddClick={(s) => setAddingFor(s)}
            />
          ))}
        </div>
      </DndContext>

      {addingFor && (
        <div className="border border-mx-grey-800 bg-mx-grey-900 p-mx-3">
          <p className="mono-label mb-mx-2 text-mx-grey-mid">
            ADD TO {addingFor.replace('_', ' ')}
          </p>
          <QuickCapture status={addingFor} onCreated={() => setAddingFor(null)} />
        </div>
      )}
    </div>
  );
}
